import moment from 'moment';

/**
 * Single source of truth for retail-site date/shift logic shared between the
 * Guarding and Access request flows. Add new retail-specific rules here so we
 * don't end up fixing the same bug in two places.
 */

export interface RetailOpenDay {
    day: string;
    startTime: string;
    endTime: string;
    /** Guarding only: the store is shut this day and the hours are borrowed */
    closed?: boolean;
    /** Weekday the borrowed hours came from, e.g. "Saturday" for a closed Sunday */
    hoursFrom?: string | null;
}

export type OpenDaysByDay = Record<string, RetailOpenDay>;

interface RawShift {
    type: string;
    typeId: string | number;
    startTime: string;
    endTime: string;
    day?: number | string | null;
}

interface ShiftTimeSlot {
    label: string;
    startTime: string;
    endTime: string;
    day?: number | null;
}

export interface RetailShiftOption {
    id: number;
    name: string;
    label?: string;
    value: string;
    timeSlots: ShiftTimeSlot[];
}

// Optional fields are typed as `T | undefined` rather than `T?` so that
// callers compiled under `exactOptionalPropertyTypes` (where `startTime?:
// string` differs from `startTime?: string | undefined`) still satisfy the
// generic constraint when their own visit-date types declare those fields
// as explicitly nullable.
interface VisitDateLike {
    date: string;
    saved?: boolean | undefined;
    startTime?: string | undefined;
}

/**
 * Format a "HH:mm:ss" string to "hh:mm AM/PM" (uppercase). Identical to the
 * formatTime helper that previously lived inline in both forms.
 *
 * @param  timeString
 * @return formatted time
 */
export const formatShiftTime = (timeString: string): string => {
    const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
    const formattedTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], options);
    return formattedTime.replace(/\b([ap])m\b/gi, (match) => match.toUpperCase());
};

/**
 * Transform raw shift rows into ShiftSelector options. Optionally filter to
 * specific day-of-week numbers (0=Sun..6=Sat); shifts with day=null|undefined
 * apply to all days. Time slots that share a start/end pair are deduped so a
 * site with multiple per-day rows for the same shift type doesn't render
 * duplicate entries in the Time Slots dropdown. Always appends a "Custom"
 * option last.
 *
 * Pass keepPerDaySlots for retail, where no slot is picked and each date is
 * booked on its own weekday's hours: two weekdays that happen to open at the
 * same time must still keep a slot each, or the second weekday has none to
 * resolve against.
 *
 * @param  shifts
 * @param  filterDayNums
 * @param  keepPerDaySlots
 * @return shift options
 */
export function buildShiftOptions(
    shifts: RawShift[],
    filterDayNums?: number[],
    keepPerDaySlots = false,
): RetailShiftOption[] {
    const filtered = filterDayNums && filterDayNums.length > 0
        ? shifts.filter((s) => s.day === null || s.day === undefined || filterDayNums.includes(Number(s.day)))
        : shifts;

    const out: RetailShiftOption[] = [];
    filtered.forEach((shift, idx) => {
        const existing = out.find((o) => o.name === shift.type);
        const timeSlot: ShiftTimeSlot = {
            label: `${formatShiftTime(shift.startTime)} - ${formatShiftTime(shift.endTime)}`,
            startTime: shift.startTime,
            endTime: shift.endTime,
            day: typeof shift.day === 'number' ? shift.day : null,
        };

        if (!existing) {
            out.push({
                id: idx,
                name: shift.type,
                label: shift.type,
                value: String(shift.typeId),
                timeSlots: [timeSlot],
            });
            return;
        }

        const alreadyExists = existing.timeSlots.some(
            (ts) => ts.startTime === shift.startTime
                && ts.endTime === shift.endTime
                && (!keepPerDaySlots || ts.day === timeSlot.day),
        );
        if (!alreadyExists) {
            existing.timeSlots.push(timeSlot);
        }
    });

    out.push({
        id: 0,
        name: 'Custom',
        value: '30',
        timeSlots: [],
    });

    return out;
}

/**
 * Retail guarding is booked night-first, so Night Shift leads the shift-type
 * dropdown, then Day Shift, then 24/7. Leading the list is all it does: the
 * dropdown opens with nothing selected, because picking a shift books it.
 * Any other shift type keeps its existing relative order behind those
 * three, which leaves "Custom" last where buildShiftOptions appended it.
 *
 * 24/7 is ranked rather than left to arrive in the right place on its own: the
 * options come back in whatever order the site's shift rows were created in.
 *
 * @param  options
 * @return reordered shift options
 */
export function orderRetailShiftOptions<T extends { name: string }>(options: T[]): T[] {
    const rank = (name: string): number => {
        if (name === 'Night Shift') {
            return 0;
        }
        if (name === 'Day Shift') {
            return 1;
        }
        if (name === '24/7') {
            return 2;
        }
        return 3;
    };

    // Sort is stable, so same-rank options stay in the order they arrived in.
    return [...options].sort((a, b) => rank(a.name) - rank(b.name));
}

/**
 * Compute the day-of-week filter for retail sites: returns the unique day
 * numbers (0..6) of any unsaved visit dates so the shift list narrows to
 * shifts configured for those days. Returns undefined for non-retail sites
 * or when there are no unsaved dates.
 *
 * @param  visitDates
 * @param  isRetail
 * @return day numbers or undefined
 */
export function getRetailDayFilter(
    visitDates: VisitDateLike[],
    isRetail: boolean,
): number[] | undefined {
    if (!isRetail) {
        return undefined;
    }
    const unsavedDayNums = visitDates
        .filter((vd) => !vd.saved)
        .map((vd) => moment(vd.date, 'MM-DD-YYYY').day());
    return unsavedDayNums.length > 0 ? unsavedDayNums : undefined;
}

export interface ResolvedRetailShift {
    startTime: string;
    endTime: string;
    shiftName: string;
    shiftType: number;
}

/**
 * For a retail site, resolve the day-specific shift row (matching the visit
 * date's weekday and the user-picked shift type name) so the saved date
 * inherits that day's actual start/end times. Returns null when no row
 * matches — the caller should treat that as "shift not available for this
 * day" and surface a notification.
 *
 * @param  shifts         raw shifts from stepData (each row has a `day` 0..6)
 * @param  dateString     visit date in MM-DD-YYYY
 * @param  pickedShiftName shift type the user selected (e.g. "Day Shift")
 * @return resolved shift or null
 */
export function resolveRetailShiftForDate(
    shifts: RawShift[],
    dateString: string,
    pickedShiftName: string,
): ResolvedRetailShift | null {
    const dayNum = moment(dateString, 'MM-DD-YYYY').day();
    // Match by shift name since typeId can be 0 (falsy) which gets lost
    // through the dropdown component.
    const dayShift = shifts.find(
        (s) => s.type === pickedShiftName && Number(s.day) === dayNum,
    );
    if (!dayShift) {
        return null;
    }
    return {
        startTime: dayShift.startTime,
        endTime: dayShift.endTime,
        shiftName: dayShift.type,
        shiftType: parseInt(String(dayShift.typeId), 10),
    };
}

/**
 * Drop newly-added dates that have no opening times configured for their
 * weekday. Returns the validated visit-dates array plus the human-formatted
 * list of dates that were removed (so the caller can surface a notification).
 * Saved dates and dates with a start time already set are left untouched.
 *
 * @param  uniqueVisitDates
 * @param  newlyAddedDates
 * @param  openDaysByDay
 * @return validation result
 */
export function validateDatesAgainstOpenDays<T extends VisitDateLike>(
    uniqueVisitDates: T[],
    newlyAddedDates: Set<string>,
    openDaysByDay: OpenDaysByDay,
): { validated: T[]; droppedDates: string[] } {
    const droppedDates: string[] = [];
    const validated = uniqueVisitDates.filter((vd) => {
        if (vd.saved || vd.startTime || !newlyAddedDates.has(vd.date)) {
            return true;
        }
        const dayName = moment(vd.date, 'MM-DD-YYYY').format('dddd').toLowerCase();
        if (!openDaysByDay[dayName]) {
            droppedDates.push(moment(vd.date, 'MM-DD-YYYY').format('DD/MM/YYYY'));
            return false;
        }
        return true;
    });
    return { validated, droppedDates };
}

// Matches SiteShift::TYPE_ALL_DAY, the shift type saved dates are stamped with.
const RETAIL_247_TYPE_ID = '20';

const WEEKDAY_NAMES = [
    'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

/**
 * Build the retail 24/7 shift option from the store's opening times.
 *
 * Derived rather than read off the site: a retail 24/7 booking follows whatever
 * hours the store keeps, so there is no 24/7 shift row on the site to read, and
 * generating one per weekday would put rows like "Sunday 24/7" on every store.
 *
 * Only days the store is open — and open again the day after, so the closed
 * block has somewhere to end — get a time slot, and a day with no coverable
 * slot produces no option at all rather than an unpickable one.
 *
 * @param  openDaysByDay  Opening windows keyed by lowercase weekday name
 * @param  filterDayNums  Restrict to these weekday numbers (0=Sun..6=Sat)
 * @return the 24/7 option, or null when no day can be covered
 */
export function buildRetail247Option(
    openDaysByDay: OpenDaysByDay | undefined,
    filterDayNums?: number[],
): RetailShiftOption | null {
    if (!openDaysByDay) {
        return null;
    }

    const days = filterDayNums && filterDayNums.length > 0
        ? [...new Set(filterDayNums)]
        : WEEKDAY_NAMES.map((_, index) => index);

    const timeSlots: ShiftTimeSlot[] = [];

    days.forEach((dayNum) => {
        const today = openDaysByDay[WEEKDAY_NAMES[dayNum] ?? ''];
        const nextDay = openDaysByDay[WEEKDAY_NAMES[(dayNum + 1) % 7] ?? ''];

        if (!today?.startTime || !nextDay?.startTime) {
            return;
        }

        const alreadyListed = timeSlots.some(
            (ts) => ts.startTime === today.startTime && ts.endTime === nextDay.startTime,
        );
        if (alreadyListed) {
            return;
        }

        timeSlots.push({
            label: `${formatShiftTime(today.startTime)} - ${formatShiftTime(nextDay.startTime)}`,
            startTime: today.startTime,
            endTime: nextDay.startTime,
            day: dayNum,
        });
    });

    if (timeSlots.length === 0) {
        return null;
    }

    return {
        id: -1,
        name: '24/7',
        label: '24/7',
        value: RETAIL_247_TYPE_ID,
        timeSlots,
    };
}

/**
 * Put the derived 24/7 option into a retail site's shift list, replacing any
 * 24/7 row the site happens to hold: those carry fixed hours (usually 7am-7pm),
 * which is not what a retail 24/7 booking follows, and leaving one in would
 * list the shift twice.
 *
 * Returns the options untouched when no day can be covered, so the option is
 * simply not offered rather than offered and then refusing every date.
 *
 * @param  options        Shift options built from the site's shift rows
 * @param  openDaysByDay  Opening windows keyed by lowercase weekday name
 * @param  filterDayNums  Restrict to these weekday numbers (0=Sun..6=Sat)
 * @return options including the derived 24/7 entry
 */
export function applyRetail247Option<T extends { name: string }>(
    options: T[],
    openDaysByDay: OpenDaysByDay | undefined,
    filterDayNums?: number[],
): (T | RetailShiftOption)[] {
    const derived = buildRetail247Option(openDaysByDay, filterDayNums);

    if (!derived) {
        return options;
    }

    return [...options.filter((option) => option.name !== '24/7'), derived];
}

export interface Retail247Block {
    startTime: string;
    endTime: string;
    date: string;
    blockDate: string;
}

/**
 * Where a retail 24/7 cover starts unless the user moves it: the store's
 * opening on the earliest of the dates it covers.
 *
 * @param  dates          MM-DD-YYYY
 * @param  openDaysByDay  Opening windows keyed by lowercase weekday name
 * @return HH:mm:ss, or null when there is no date or no window for it
 */
export function retailCoverDefaultStart(dates: string[], openDaysByDay: OpenDaysByDay | undefined): string | null {
    if (!openDaysByDay || dates.length === 0) {
        return null;
    }
    const first = [...dates].sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf())[0];
    const day = openDaysByDay[moment(first, 'MM-DD-YYYY').format('dddd').toLowerCase()];

    return day?.startTime || null;
}

/**
 * Minutes past midnight, so two opening times are compared as times rather
 * than as strings. An unpadded "9:00" sorts ABOVE "16:30" lexicographically,
 * which would put a morning start after the night handover. Same rule as
 * earliestOpeningTime below and as core/utils/billedHours.
 *
 * @param  time "HH:mm:ss", "HH:mm", or either without the leading zero
 * @return minutes from midnight
 */
const minutesOfDay = (time: string): number => {
    const parsed = moment(time, ['HH:mm:ss', 'HH:mm']);

    return parsed.hours() * 60 + parsed.minutes();
};

/**
 * When the night guard takes over on a retail day: an hour before closing,
 * which is where the store's Night shift starts. A store with no hours has no
 * closing time, only the 7-to-7 grid, so its line is the grid boundary itself.
 *
 * @param  day Opening window for the date
 * @return HH:mm:ss
 */
export function retailNightStart(day: RetailOpenDay): string {
    if (day.closed && !day.hoursFrom) {
        return day.endTime;
    }
    return moment(day.endTime, 'HH:mm:ss').subtract(1, 'hour').format('HH:mm:ss');
}

/**
 * Split one date of a retail 24/7 booking into its two blocks, following the
 * store's own hours rather than the 7am-7pm grid used off the high street:
 *
 *   open  → close                 (store trading)
 *   close → next day's opening    (store shut)
 *
 * Both blocks carry the picked date as their blockDate so the form keeps them
 * together as one 24-hour booking.
 *
 * The day splits where the Night shift starts, an hour before closing, so a
 * 24/7 lines up with the store's Day and Night shifts. A store with no hours
 * runs on the 7-to-7 grid instead, which splits on the grid line itself.
 *
 * A start time moves the first block's start (the cover's first day): from
 * there to the split, or straight through to the next opening when the start
 * is already past the split.
 *
 * Returns null when the store is shut on the date itself or on the day after —
 * the closed block has nowhere to end — so the caller can skip that date.
 *
 * @param  date           Visit date in MM-DD-YYYY (calendar) format
 * @param  openDaysByDay  Opening windows keyed by lowercase weekday name
 * @param  startTime      HH:mm:ss the cover starts at on this date, if not the store's
 * @return the blocks, or null when the date cannot be covered
 */
export function buildRetail247Blocks(
    date: string,
    openDaysByDay: OpenDaysByDay | undefined,
    startTime?: string | null,
): Retail247Block[] | null {
    if (!openDaysByDay) {
        return null;
    }

    const day = moment(date, 'MM-DD-YYYY');
    const today = openDaysByDay[day.format('dddd').toLowerCase()];
    const nextDay = openDaysByDay[day.clone().add(1, 'day').format('dddd').toLowerCase()];

    if (!today?.startTime || !today?.endTime || !nextDay?.startTime) {
        return null;
    }

    const first = startTime || today.startTime;

    // A store that never shuts (open time == close time) has no closing point
    // to split on, so the day is one continuous block. Splitting it anyway
    // would produce two zero-length shifts.
    if (minutesOfDay(today.startTime) === minutesOfDay(today.endTime)) {
        return [{
            startTime: first,
            endTime: nextDay.startTime,
            date,
            blockDate: date,
        }];
    }

    const splitAt = retailNightStart(today);

    if (minutesOfDay(first) >= minutesOfDay(splitAt)) {
        return [{
            startTime: first,
            endTime: nextDay.startTime,
            date,
            blockDate: date,
        }];
    }

    return [
        {
            startTime: first,
            endTime: splitAt,
            date,
            blockDate: date,
        },
        {
            startTime: splitAt,
            endTime: nextDay.startTime,
            date,
            blockDate: date,
        },
    ];
}

/**
 * The store's opening time for a retail general visit, which is where the shift
 * should start. When several dates are booked in one go the stores' opening
 * times can differ by weekday, so the EARLIEST of them is used — a start that is
 * valid for every date picked.
 *
 * Returns null when none of the dates map to an open day, so the caller can
 * leave the picker empty rather than inventing a time.
 *
 * @param  dates          Selected dates in MM-DD-YYYY (calendar) format
 * @param  openDaysByDay  Opening windows keyed by lowercase weekday name
 * @return Opening time as HH:mm:ss, or null
 */
export function earliestOpeningTime(
    dates: string[],
    openDaysByDay: OpenDaysByDay | undefined,
): string | null {
    if (!openDaysByDay || dates.length === 0) {
        return null;
    }

    const openings = dates
        .map((date) => openDaysByDay[moment(date, 'MM-DD-YYYY').format('dddd').toLowerCase()]?.startTime)
        .filter((time): time is string => !!time);

    if (openings.length === 0) {
        return null;
    }

    // Compared as times, not strings: an unpadded "9:00" sorts ABOVE "10:00"
    // lexicographically and would pick the later opening. Same rule as
    // core/utils/billedHours.
    return openings.reduce((earliest, time) => (
        moment(time, 'HH:mm:ss').isBefore(moment(earliest, 'HH:mm:ss')) ? time : earliest
    ));
}

/**
 * The start time a retail general visit should default to, given both places a
 * date selection can come from on the date step.
 *
 * The step has a calendar and, below it, a "Select Dates for Chosen Shift"
 * dropdown that narrows the calendar selection. The default used to be driven
 * by the dropdown alone, which left the time blank until the user opened it
 * even though the dates were already picked and the opening time was already
 * knowable. So: use the dropdown once the user has narrowed it, and the
 * calendar selection before that.
 *
 * Either way the time comes from earliestOpeningTime, so a single date gets its
 * own opening time and a bulk selection gets the earliest across the dates,
 * which is the only start valid for all of them.
 *
 * @param  dropdownDates  Dates ticked in the shift dropdown, MM-DD-YYYY
 * @param  calendarDates  Unsaved dates picked on the calendar, MM-DD-YYYY
 * @param  openDaysByDay  Opening windows keyed by lowercase weekday name
 * @return Opening time as HH:mm:ss, or null when no date maps to an open day
 */
export function retailGeneralDefaultStartTime(
    dropdownDates: string[],
    calendarDates: string[],
    openDaysByDay: OpenDaysByDay | undefined,
): string | null {
    const source = dropdownDates.length > 0 ? dropdownDates : calendarDates;

    return earliestOpeningTime(source, openDaysByDay);
}
